import React from 'react';
import { Box, Typography } from '@mui/material';
import { elliottTokens as t } from '../tokens';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';

// Lesson Complete strip — Cian feedback v2 layout: Elliott orb (left) +
// structured rows (Title / Quiz / Live Scenario / Streak) + two CTAs.
// Row rules: the Quiz row is hidden when the lesson has no MCQ, the Live
// Scenario row when the lesson has none (e.g. RDP Creative programs) —
// never render empty or 0% states. Running Score = the learner's average
// across all completed quizzes in the course (BE calc in production).
// One motion per moment: the slide-up is the celebration.

export interface LessonCompleteStripProps {
  /** Module the lesson sits in; null when the nav can't name it, in which
      case the title reads "Lesson Complete: [lesson]" rather than inventing
      a module name. */
  moduleName: string | null;
  lessonName: string;
  /** % score on this lesson's quiz; null/0 hides the Quiz row. */
  lessonScorePct: number | null;
  /** Course-level average across completed quizzes; omitted when 0. */
  runningScorePct: number | null;
  /** True when the lesson had a Live Scenario the learner submitted. */
  scenarioSubmitted: boolean;
  /** Live streak from the summary endpoint; null hides the row (no
      placeholder numbers — hide-when-absent, Cian v2). */
  weeksActive: number | null;
  /** Omitted when there is no next lesson to open — the button is then not
      rendered at all, rather than offered and quietly going somewhere else. */
  onNext?: () => void;
  onDone: () => void;
}

// Elliott orb — the doc's mascot (soft round body, two eyes, smile),
// left-aligned per the v2 spec. Static inline SVG; the animated/floating
// variant lives in ElliottOrb.tsx.
const OrbFace: React.FC = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden>
    <defs>
      <linearGradient id="lcsOrbGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#F79420" />
        <stop offset="1" stopColor="#F15A26" />
      </linearGradient>
    </defs>
    <path d="M24 3 C36 3 45 10 45 24 C45 38 38 45 24 45 C10 45 3 38 3 24 C3 10 12 3 24 3 Z" fill="url(#lcsOrbGrad)" />
    <circle cx="17.5" cy="21" r="2.6" fill="#2A1204" />
    <circle cx="30.5" cy="21" r="2.6" fill="#2A1204" />
    <path d="M17 29 Q24 34.5 31 29" stroke="#2A1204" strokeWidth="2.4" strokeLinecap="round" fill="none" />
  </svg>
);

const rowLabelSx = {
  width: 100,
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1px',
  color: t.textTertiary,
  textTransform: 'uppercase'
} as const;

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', mt: '5px' }}>
    <Typography sx={rowLabelSx}>{label}</Typography>
    <Typography sx={{ fontSize: 13.5, color: t.textSecondary }}>{children}</Typography>
  </Box>
);

const LessonCompleteStrip: React.FC<LessonCompleteStripProps> = ({
  moduleName,
  lessonName,
  lessonScorePct,
  runningScorePct,
  scenarioSubmitted,
  weeksActive,
  onNext,
  onDone
}) => {
  const showQuizRow = lessonScorePct != null && lessonScorePct > 0;
  const showRunning = runningScorePct != null && runningScorePct > 0;

  return (
    <Box
      onClick={onDone}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        bgcolor: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        px: 3,
        pb: '28px',
        // Phone: a sheet along the bottom, clear of the home indicator.
        [MOBILE_SX]: { px: '12px', pb: 'max(12px, env(safe-area-inset-bottom))' },
        animation: 'lessonCompleteFade 180ms ease-out',
        '@keyframes lessonCompleteFade': {
          from: { opacity: 0 },
          to: { opacity: 1 }
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: 760,
          maxWidth: '100%',
          bgcolor: t.surface,
          border: `1px solid ${t.success}`,
          borderRadius: '12px',
          p: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '18px',
          // Phone: the actions take a full-width row under the summary.
          [MOBILE_SX]: { flexWrap: 'wrap', p: '16px', gap: '14px' },
          animation: 'lessonCompleteRise 220ms ease-out',
          '@keyframes lessonCompleteRise': {
            from: { transform: 'translateY(24px)', opacity: 0 },
            to: { transform: 'translateY(0)', opacity: 1 }
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
        }}
      >
        <Box sx={{ flexShrink: 0, alignSelf: 'flex-start', mt: '2px' }}>
          <OrbFace />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 17,
              fontWeight: 700,
              color: t.textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              [MOBILE_SX]: { whiteSpace: 'normal', lineHeight: 1.3 }
            }}
          >
            Lesson Complete: {moduleName ? `${moduleName}: ` : ''}
            {lessonName}
          </Typography>

          {showQuizRow && (
            <InfoRow label="Quiz">
              Lesson score:{' '}
              <Box component="span" sx={{ color: t.textPrimary, fontWeight: 600 }}>
                {lessonScorePct}%
              </Box>
              {showRunning && (
                <>
                  {' · '}Running score:{' '}
                  <Box component="span" sx={{ color: t.textPrimary, fontWeight: 600 }}>
                    {runningScorePct}%
                  </Box>
                </>
              )}
            </InfoRow>
          )}

          {scenarioSubmitted && <InfoRow label="Roleplay">Submitted</InfoRow>}

          {weeksActive !== null && (
            <InfoRow label="Streak">
              <Box component="span" sx={{ color: t.brandLight, fontWeight: 600 }}>
                {weeksActive} {weeksActive === 1 ? 'Week' : 'Weeks'}
              </Box>
            </InfoRow>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: '8px',
            [MOBILE_SX]: { flexBasis: '100%', '& > *': { minHeight: 44 } }
          }}
        >
          {onNext && (
            <Box
              onClick={onNext}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: '22px',
                py: '9px',
                borderRadius: '8px',
                cursor: 'pointer',
                color: t.textPrimary,
                fontWeight: 600,
                fontSize: 14,
                background: t.brandyTextGradient,
                transition: 'filter 0.15s ease',
                '&:hover': { filter: 'brightness(1.08)' }
              }}
            >
              Next Lesson
            </Box>
          )}
          {/* Finishing the last lesson of a program leaves this as the only
              action, so it takes the primary styling rather than leaving the
              celebration with nothing but an outline. */}
          <Box
            onClick={onDone}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '22px',
              py: onNext ? '8px' : '9px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: onNext ? t.textSecondary : t.textPrimary,
              fontWeight: onNext ? 500 : 600,
              fontSize: onNext ? 13 : 14,
              ...(onNext
                ? {
                    border: `1px solid ${t.borderContrast}`,
                    transition: 'border-color 0.15s ease, color 0.15s ease',
                    '&:hover': { borderColor: t.textSecondary, color: t.textPrimary }
                  }
                : {
                    background: t.brandyTextGradient,
                    transition: 'filter 0.15s ease',
                    '&:hover': { filter: 'brightness(1.08)' }
                  })
            }}
          >
            Done For Today
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LessonCompleteStrip;
